import { useEffect, useMemo, useRef, useState } from "react";
import { detectInsights, suggestChartSpecs, type ChartKind, type ChartSpec } from "@brasilnovo/shared";
import { api } from "../lib/api.js";
import {
  uid,
  useExplorerSessions,
  type AgentPayload,
  type ExplorerMessage,
  type ExplorerSession,
} from "../lib/explorerStore.js";
import { ChartRenderer } from "../components/ChartRenderer.js";

const EXAMPLES = [
  "Tendência de viagens por competência",
  "Faturamento de frete por região",
  "Metas por região e status",
  "Top cavalos por número de viagens",
  "Quais motoristas com exame vencido?",
  "Distribuição de inconsistências por tipo",
];

const KIND_LABEL: Record<ChartKind, string> = {
  line: "Linha", area: "Área", bar: "Barras", stackedBar: "Empilhado",
  groupedBar: "Agrupado", horizontalBar: "Ranking", pie: "Pizza",
  scatter: "Dispersão", kpi: "Indicador", table: "Tabela",
};
const KIND_ICON: Record<ChartKind, string> = {
  line: "📈", area: "🏔", bar: "📊", stackedBar: "🧱", groupedBar: "📊",
  horizontalBar: "📶", pie: "🥧", scatter: "✴", kpi: "🔢", table: "▦",
};

export function ExplorarIA() {
  const store = useExplorerSessions();
  const { active, storageFull } = store;

  // dvh (não vh) acompanha a barra de endereço no mobile; min-h evita colapso.
  return (
    <div className="flex h-[calc(100dvh-3rem)] min-h-[32rem] flex-col gap-3">
      {storageFull && (
        <div className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
          ⚠ Armazenamento local cheio — os estudos mais antigos não guardam mais os dados dos gráficos (o histórico de texto é preservado).
        </div>
      )}
      <div className="flex min-h-0 flex-1 gap-4">
        <SessionRail store={store} />
        <section className="flex min-w-0 flex-1 flex-col rounded-xl border border-slate-200 bg-white">
          {active ? (
            <StudyView key={active.id} session={active} store={store} />
          ) : (
            <EmptyWorkspace onCreate={store.create} />
          )}
        </section>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Rail (estudos)

function SessionRail({ store }: { store: ReturnType<typeof useExplorerSessions> }) {
  const { sessions, activeId, setActiveId, create, rename, remove } = store;
  const [editing, setEditing] = useState<string | null>(null);

  return (
    <aside className="flex w-64 shrink-0 flex-col rounded-xl border border-slate-200 bg-white">
      <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
        <span className="text-sm font-semibold text-slate-700">Estudos</span>
        <button
          className="flex h-7 w-7 items-center justify-center rounded-lg bg-brand-600 text-lg leading-none text-white hover:bg-brand-700"
          title="Novo estudo"
          aria-label="Novo estudo"
          onClick={create}
        >
          +
        </button>
      </div>
      <div className="flex-1 space-y-1 overflow-y-auto p-2">
        {sessions.length === 0 && (
          <p className="px-2 py-6 text-center text-xs text-slate-400">
            Nenhum estudo ainda. Clique em <span className="font-semibold">+</span> para começar.
          </p>
        )}
        {sessions.map((s) => (
          <div
            key={s.id}
            className={`group flex items-center gap-1 rounded-lg px-2 py-2 text-sm ${
              s.id === activeId ? "bg-brand-50 text-brand-700" : "text-slate-600 hover:bg-slate-50"
            }`}
          >
            {editing === s.id ? (
              <input
                autoFocus
                defaultValue={s.title}
                className="w-full rounded border border-brand-300 px-1.5 py-0.5 text-sm outline-none"
                onBlur={(e) => { rename(s.id, e.target.value.trim()); setEditing(null); }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                  if (e.key === "Escape") setEditing(null);
                }}
              />
            ) : (
              <>
                <button className="min-w-0 flex-1 truncate text-left" onClick={() => setActiveId(s.id)} title={s.title}>
                  {s.title}
                  <span className="block text-[10px] text-slate-400">{s.messages.length} msg</span>
                </button>
                <button className="hidden text-xs text-slate-400 hover:text-slate-600 group-hover:block" title="Renomear" aria-label={`Renomear ${s.title}`} onClick={() => setEditing(s.id)}>✎</button>
                <button className="hidden text-xs text-slate-400 hover:text-red-600 group-hover:block" title="Excluir" aria-label={`Excluir ${s.title}`} onClick={() => { if (confirm(`Excluir "${s.title}"?`)) remove(s.id); }}>🗑</button>
              </>
            )}
          </div>
        ))}
      </div>
    </aside>
  );
}

function EmptyWorkspace({ onCreate }: { onCreate: () => string }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 p-10 text-center">
      <div className="text-4xl">🧭</div>
      <h2 className="text-xl font-bold text-slate-800">Explorar com IA</h2>
      <p className="max-w-md text-sm text-slate-500">
        Faça perguntas em linguagem natural sobre a operação. Os dados são plotados
        na hora e você troca o tipo de gráfico em tempo real. Cada estudo guarda a
        conversa localmente no seu navegador.
      </p>
      <button className="btn-primary" onClick={onCreate}>+ Iniciar novo estudo</button>
    </div>
  );
}

// ---------------------------------------------------------------- Study (chat)

function StudyView({ session, store }: { session: ExplorerSession; store: ReturnType<typeof useExplorerSessions> }) {
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const threadRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    threadRef.current?.scrollTo({ top: threadRef.current.scrollHeight, behavior: "smooth" });
  }, [session.messages.length]);

  async function ask(question: string) {
    const text = question.trim();
    if (text.length < 3 || loading) return;
    setQ("");
    setLoading(true);
    store.addMessage(session.id, { id: uid(), role: "user", text, ts: Date.now() });
    const aid = uid();
    store.addMessage(session.id, { id: aid, role: "assistant", pending: true, ts: Date.now() });
    try {
      const { data } = await api.post<AgentPayload>("/agent/ask", { question: text });
      // Defesa: se o back não trouxe chartSpecs, infere no front a partir das linhas.
      const specs = data.chartSpecs?.length ? data.chartSpecs : suggestChartSpecs(data.columns, data.rows);
      store.patchMessage(session.id, aid, {
        pending: false,
        answer: { ...data, chartSpecs: specs },
        selectedKind: specs[0]?.kind ?? "table",
        text: data.answer,
      });
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? "Erro ao consultar o agente";
      store.patchMessage(session.id, aid, { pending: false, error: msg });
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <header className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
        <div className="min-w-0">
          <div className="truncate font-semibold text-slate-800">{session.title}</div>
          <div className="text-xs text-slate-400">Explorar com IA · gráficos em tempo real</div>
        </div>
      </header>

      <div ref={threadRef} className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
        {session.messages.length === 0 && (
          <div className="mx-auto max-w-2xl pt-6">
            <p className="mb-3 text-sm text-slate-500">Comece com um exemplo:</p>
            <div className="flex flex-wrap gap-2">
              {EXAMPLES.map((ex) => (
                <button key={ex} className="btn-ghost text-xs" onClick={() => ask(ex)}>{ex}</button>
              ))}
            </div>
          </div>
        )}

        {session.messages.map((m) =>
          m.role === "user" ? (
            <div key={m.id} className="flex justify-end">
              <div className="max-w-[80%] rounded-2xl rounded-br-sm bg-brand-600 px-4 py-2 text-sm text-white">{m.text}</div>
            </div>
          ) : (
            <AssistantBubble key={m.id} message={m} onSelectKind={(k) => store.patchMessage(session.id, m.id, { selectedKind: k })} />
          ),
        )}
      </div>

      <div className="border-t border-slate-200 p-3">
        <div className="flex gap-2">
          <input
            className="input"
            placeholder="Peça dados ou um gráfico: 'frete por região', 'tendência de viagens'…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && ask(q)}
          />
          <button className="btn-primary shrink-0" onClick={() => ask(q)} disabled={loading || q.trim().length < 3}>
            {loading ? "…" : "Enviar"}
          </button>
        </div>
      </div>
    </>
  );
}

function AssistantBubble({ message, onSelectKind }: { message: ExplorerMessage; onSelectKind: (k: ChartKind) => void }) {
  // Hook sempre no topo (regras dos hooks): tipos de gráfico únicos sugeridos.
  const kinds = useMemo(() => {
    const seen = new Set<ChartKind>();
    return (message.answer?.chartSpecs ?? []).filter((s) => (seen.has(s.kind) ? false : (seen.add(s.kind), true)));
  }, [message.answer]);

  if (message.pending) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-400">
        <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-brand-600" />
        consultando os dados…
      </div>
    );
  }
  if (message.error) {
    return <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">⚠ {message.error}</div>;
  }
  const a = message.answer;
  if (!a) return null;

  const selected: ChartSpec =
    a.chartSpecs.find((s) => s.kind === message.selectedKind) ?? a.chartSpecs[0] ?? { kind: "table", series: [] };

  const hasData = a.rows.length > 0;

  return (
    <div className="max-w-full rounded-2xl rounded-bl-sm bg-slate-50 p-4">
      <div className="mb-2 text-sm text-slate-600">{a.answer}</div>

      {hasData && (
        <>
          {kinds.length > 1 && (
            <div className="mb-3 flex flex-wrap gap-1.5">
              {kinds.map((s) => (
                <button
                  key={s.kind}
                  className={`rounded-full px-2.5 py-1 text-xs font-medium transition ${
                    s.kind === selected.kind ? "bg-brand-600 text-white" : "bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-100"
                  }`}
                  title={s.reason}
                  onClick={() => onSelectKind(s.kind)}
                >
                  {KIND_ICON[s.kind]} {KIND_LABEL[s.kind]}
                </button>
              ))}
            </div>
          )}

          {(() => {
            const ins = detectInsights(selected, a.rows, a.columns);
            return ins.length ? (
              <div className="mb-2 flex flex-wrap gap-1.5">
                {ins.map((x, i) => (
                  <span key={i} className={`inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium ring-1 ${
                    x.severity === "critical" ? "bg-red-50 text-red-700 ring-red-200"
                    : x.severity === "warning" ? "bg-amber-50 text-amber-800 ring-amber-200"
                    : "bg-blue-50 text-blue-700 ring-blue-200"}`}>
                    {x.severity === "critical" ? "⛔" : x.severity === "warning" ? "⚠" : "💡"} {x.text}
                  </span>
                ))}
              </div>
            ) : null;
          })()}

          <div className="rounded-xl border border-slate-200 bg-white p-3">
            <ChartRenderer spec={selected} rows={a.rows} columns={a.columns} />
          </div>

          {selected.reason && selected.kind !== "table" && (
            <div className="mt-1.5 text-xs text-slate-400">Por que este gráfico: {selected.reason}.</div>
          )}
        </>
      )}

      <details className="mt-2">
        <summary className="cursor-pointer text-xs text-slate-400">
          {hasData ? `${a.rows.length} linha(s) · ` : ""}SQL · fonte: {a.citation}
        </summary>
        <pre className="mt-1 overflow-x-auto rounded bg-slate-900 p-2 text-xs text-slate-100">{a.sql}</pre>
      </details>
    </div>
  );
}
